import { useMemo, useState } from "react";
import { App } from "antd5";
import { Flex } from "styles/utility-components";

import { providerIconColorMap, providerIconKeyMap } from "components/integrations/ProviderButtons";
import Button, { ButtonStyle, ButtonType } from "components/ui/button/Button";
import { ProviderTypes } from "lib/generated/integration-api";
import { useConnectedParagonCrm } from "lib/hooks/api/integrations/useConnectedParagonCrm";
import { useDescribeContactDetails } from "lib/hooks/api/integrations/useContactDetails";
import { useSendContact } from "lib/hooks/api/integrations/useSendContact";
import { refusedFieldsFrom } from "lib/integrations/crmSendErrors";
import { EventNames, useTracking } from "lib/tracking";
import { crmContactFieldsFrom } from "./sendableContacts";
import { ContactData } from "./SendContactToCRMButton";
import { CrmRecord, SendToParagonCrmModal } from "./SendToParagonCrmModal";
import { crmCognismContactId } from "./util";

type Props = {
  contact: ContactData;
  /**
   * Passed through to every button below, so this sits the same way as the native one does wherever
   * it is used: the lead card flattens its menu items with `inverse`, and dropping the style is
   * what leaves a filled button among them.
   */
  buttonType?: ButtonType;
  buttonStyle?: ButtonStyle;
  fullWidth?: boolean;
  justifyContent?: string;
  disabled?: boolean;
  /** Run instead of following the link when disabled, to prompt as the native button does. */
  onBlocked?: () => void;
};

/**
 * The contact's action for the CRM connected through Paragon: a link to the record once it has been
 * pushed, otherwise a form to review before pushing.
 *
 * A form rather than the native providers' single click, because the fields come from the company's
 * own mapping — so what will be written is worth showing before it is.
 */
export function SendContactToParagonCrm({
  contact,
  buttonType = "secondary",
  buttonStyle,
  fullWidth,
  justifyContent,
  disabled,
  onBlocked,
}: Props): JSX.Element | null {
  const { logEvent } = useTracking();
  const provider = useConnectedParagonCrm();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const cognismContactId = crmCognismContactId(contact);
  const { data: crmDetails, isInitialLoading } = useDescribeContactDetails(
    contact.id,
    cognismContactId,
    { enabled: !!provider },
  );
  const { message } = App.useApp();
  // Read before the early return below, which the rules of hooks put after every hook call.
  const crmName = provider?.name ?? "";
  const providerType = provider?.providerType ?? ProviderTypes.Dynamics;

  // The modal stays open until the push lands, so a refusal it can name — a field the CRM will not
  // take — is shown against the form itself. This covers the rest: the flag, a malformed body.
  const { mutateAsync: sendContact } = useSendContact({
    onSuccess: (data) => {
      message.open({
        type: data.isDuplicate ? "warning" : "success",
        content: (
          <Flex gap={8} alignItems="center">
            {data.isDuplicate
              ? `Contact already exists in your ${crmName} account`
              : `Sent to ${crmName}`}
            {data.externalUrl && (
              <Button
                as="a"
                buttonType="secondary"
                href={data.externalUrl}
                target="new-tab"
                rel="noopener noreferrer"
                icon={providerIconKeyMap[providerType]}
                iconProps={{ color: providerIconColorMap[providerType] }}
              >
                View
              </Button>
            )}
          </Flex>
        ),
      });
    },
    async onError(error) {
      // Which fields is left to the modal, which is still open behind this and can name them under
      // the labels the user picked them by.
      const refused = await refusedFieldsFrom(error);
      void message.error(
        refused.length > 0
          ? `${crmName} would not accept some of these fields`
          : "Failed to send contact",
      );
    },
  });

  // Memoised because the modal seeds its form from this, and the tables build the contact inline.
  const record: CrmRecord = useMemo(
    () => ({
      kind: "contact",
      guid: contact.id,
      name: [contact.firstName, contact.lastName].filter(Boolean).join(" "),
      ...crmContactFieldsFrom(contact),
    }),
    [contact],
  );

  if (!provider) return null;

  if (isInitialLoading) {
    return (
      <Button buttonType={buttonType} buttonStyle={buttonStyle} loading fullWidth={fullWidth} />
    );
  }

  if (crmDetails?.externalUrl) {
    return (
      <Button
        as="a"
        buttonType={buttonType}
        buttonStyle={buttonStyle}
        // Blanked rather than merely unclicked, so the record is not reachable from the markup
        // either: this is the same guard the native button puts on its own link.
        href={disabled ? "" : crmDetails.externalUrl}
        target="new-tab"
        icon={providerIconKeyMap[providerType]}
        iconProps={{ color: providerIconColorMap[providerType] }}
        fullWidth={fullWidth}
        justifyContent={justifyContent}
        onClick={(event: React.MouseEvent) => {
          if (disabled) {
            event.preventDefault();
            onBlocked?.();
            return;
          }
          logEvent(EventNames.viewInCRMButtonClicked, {
            "Data types": "Contact",
            "Connected provider": providerType,
          });
        }}
      >
        View
      </Button>
    );
  }

  return (
    <>
      <Button
        buttonType={buttonType}
        buttonStyle={buttonStyle}
        icon={providerIconKeyMap[providerType]}
        iconProps={{ color: providerIconColorMap[providerType] }}
        fullWidth={fullWidth}
        justifyContent={justifyContent}
        // Not `disabled`: that swallows the click, and with it the prompt explaining why the button
        // will not do anything. The native send button handles a paywalled user the same way.
        onClick={() => {
          if (disabled) {
            onBlocked?.();
            return;
          }
          logEvent(EventNames.sendToCRMModalOpened, {
            "Data types": "Contact",
            "Connected provider": providerType,
          });
          setIsModalOpen(true);
        }}
      >
        Send
      </Button>
      <SendToParagonCrmModal
        provider={provider}
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        record={record}
        onSend={(request) =>
          sendContact({
            contactId: contact.id,
            cognismContactId,
            // Required by the route for the providers we name the fields for. The mapped push below
            // carries its own, so these are only along for the ride.
            contactCompanyName: contact.buyer?.name ?? "",
            firstName: contact.firstName ?? "",
            lastName: contact.lastName ?? "",
            entityName: request.logicalName,
            fields: JSON.stringify(request.fields),
            ...(Object.keys(request.lookups).length > 0
              ? { lookups: JSON.stringify(request.lookups) }
              : {}),
          })
        }
      />
    </>
  );
}
